import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

// ============================================================
// Page-level wrappers
// ============================================================

export const AboutPage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14mm 14mm 14mm 14mm;
  box-sizing: border-box;

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 14mm 14mm 14mm 14mm !important;
  }
`

export const PageNumberRow = styled.div`
  display: flex;
  justify-content: flex-end;
  font-family: ${omniaTheme.fonts.label};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.textLabel};
  text-transform: uppercase;

  @media print {
    display: flex !important;
    justify-content: flex-end !important;
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.textLabel} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// LEFT PAGE — Values headline
// ============================================================

export const ValuesHeadline = styled.h1`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 40px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.primary};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.2;
  margin: 16px 0 0 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 40px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.primary} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1.2 !important;
    margin: 16px 0 0 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * Wrap each LINE of the values headline (e.g. "Innovant, Créatif,") in this
 * component so the underline spans the entire line including the commas,
 * matching the customer's source design.
 */
export const ValueLine = styled.span`
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 6px;
  text-decoration-color: ${omniaTheme.colors.primary};

  /* Force the inline children rendered by EditableData (used by the rt() helper)
     to inherit the underline; otherwise EditableData's reset removes it on the
     words themselves and only the bare commas (direct text children) stay
     underlined. */
  *,
  *::before,
  *::after {
    text-decoration: inherit !important;
    text-decoration-color: inherit !important;
  }

  @media print {
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 6px !important;
    text-decoration-color: ${omniaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    *,
    *::before,
    *::after {
      text-decoration: inherit !important;
      text-decoration-color: inherit !important;
    }
  }
`

// ============================================================
// LEFT PAGE — Stats row (4 columns)
// ============================================================

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-top: 48px;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr) !important;
    gap: 32px !important;
    margin-top: 48px !important;
  }
`

export const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 6px !important;
  }
`

export const StatValue = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 24px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.primary};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 24px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.primary} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * Labels respect explicit newlines from the data (e.g. "VENTE\nPAR JOUR"
 * renders as two stacked lines) via white-space: pre-line. Reserve a min-height
 * equivalent to two lines so single-line and two-line labels share the same
 * total block height — keeps the underlines below each StatBlock aligned.
 */
export const StatLabel = styled.div`
  font-family: ${omniaTheme.fonts.label};
  font-size: 9.5px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.primary};
  text-transform: uppercase;
  line-height: 1.3;
  white-space: pre-line;
  min-height: calc(9.5px * 1.3 * 2);

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 9.5px !important;
    color: ${omniaTheme.colors.primary} !important;
    letter-spacing: ${omniaTheme.letterSpacing.wide} !important;
    text-transform: uppercase !important;
    line-height: 1.3 !important;
    white-space: pre-line !important;
    min-height: calc(9.5px * 1.3 * 2) !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const StatUnderline = styled.div`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.border};
  margin-top: 20px;

  @media print {
    width: 100% !important;
    border-top: 0.5px solid ${omniaTheme.colors.border} !important;
    margin-top: 20px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// LEFT PAGE — Service cards (3 × 2 grid)
// ============================================================

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 32px;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
    margin-top: 32px !important;
  }
`

export const ServiceCard = styled.div`
  background: ${omniaTheme.colors.cardBg};
  padding: 20px;
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 184px;
  box-sizing: border-box;

  @media print {
    background: ${omniaTheme.colors.cardBg} !important;
    padding: 20px !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
    min-height: 184px !important;
    box-sizing: border-box !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ServiceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 28px;
  height: 28px;
  color: ${omniaTheme.colors.primary};

  svg {
    display: block;
    width: 26px;
    height: 26px;
  }

  @media print {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: 28px !important;
    height: 28px !important;
    color: ${omniaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ServiceTitle = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 14px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.2;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 14px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1.2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ServiceDescription = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 9.5px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 9.5px !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// RIGHT PAGE — Map block (intro paragraph + Switzerland map)
// ============================================================

/**
 * Relative wrapper holding the map and the intro paragraph. The map fills the
 * block; the paragraph is absolutely positioned in the empty north-west corner
 * (above Lausanne / left of Neuchâtel) so the two share the same vertical band,
 * matching the customer's source layout. `flex: 1` lets it grow so the timeline
 * is pushed to the bottom of the page.
 */
export const MapBlock = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 150mm;
  margin-top: 8mm;

  @media print {
    position: relative !important;
    width: 100% !important;
    flex: 1 1 auto !important;
    min-height: 150mm !important;
    margin-top: 8mm !important;
  }
`

/** Intro paragraph — narrow column tucked into the map's north-west corner. */
export const Description = styled.p`
  position: absolute;
  top: 0;
  left: 0;
  width: 38%;
  z-index: 1;
  font-family: ${omniaTheme.fonts.body};
  font-size: 9px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;
  margin: 0;

  @media print {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 38% !important;
    z-index: 1 !important;
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 9px !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    margin: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/** Inline Switzerland map — fills the block; the SVG scales + centres. */
export const MapFigure = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  @media print {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// RIGHT PAGE — Timeline (6-column grid)
// ============================================================

/**
 * Full agency history laid out as a 6-column grid: 2015–2020 on the first row,
 * 2021–2026 on the second. `margin-top: auto` soaks up the remaining vertical
 * space so the block always sits at the bottom of the page.
 */
export const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  column-gap: 14px;
  row-gap: 28px;
  width: 100%;
  margin-top: auto;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(6, 1fr) !important;
    column-gap: 14px !important;
    row-gap: 28px !important;
    width: 100% !important;
    margin-top: auto !important;
  }
`

export const TimelineCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    text-align: left !important;
  }
`

export const TimelineYear = styled.span`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 26px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.primary};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 26px !important;
    color: ${omniaTheme.colors.primary} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/** Thin brand hairline between the year and its label — one per cell. */
export const TimelineCellLine = styled.span`
  width: 100%;
  height: 0;
  border-top: 0.5px solid ${omniaTheme.colors.strokeLine};
  margin: 12px 0 10px 0;

  @media print {
    width: 100% !important;
    border-top: 0.5px solid ${omniaTheme.colors.strokeLine} !important;
    margin: 12px 0 10px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TimelineLabel = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-size: 8px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.primary};
  text-transform: uppercase;
  line-height: 1.5;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 8px !important;
    color: ${omniaTheme.colors.primary} !important;
    letter-spacing: ${omniaTheme.letterSpacing.wide} !important;
    text-transform: uppercase !important;
    line-height: 1.5 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
